import React, { memo, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { addPageNumber } from '../common/addPageNumber';

interface DailyReconciliationPdfProps {
    data: any;
    setPdfBlob: (blob: Blob) => void;
    formData?: any;
}

// sp_DailyReconciliation_Report returns only the rows that disagree; ExceptionType 0
// (on time) never reaches us. Keep these labels in step with the CASE in the proc.
// Also used by DailyReconciliationExcel.
export const EXCEPTION_LABELS: Record<number, string> = {
    1: 'No clock out',
    2: 'Clocked out late',
    3: 'Did not clock in',
};

const DailyReconciliationPdf: React.FC<DailyReconciliationPdfProps> = ({ data, setPdfBlob, formData }) => {
    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        const pageWidth = doc.internal.pageSize.width;

        // The proc takes a single @ReportDate; the backend maps Start Date onto it and
        // falls back to today, so mirror that here rather than showing a blank heading.
        const reportDate = formData?.StartDate
            ? moment(formData.StartDate).format('MM/DD/YYYY')
            : moment().format('MM/DD/YYYY');

        doc.setFontSize(14);
        doc.text('Daily Reconciliation', 14, 15);

        doc.setFontSize(9);
        doc.text(`Report Date: ${reportDate}`, 14, 22);

        const rows: any[] = Array.isArray(data) ? data : [];

        // Counts per exception type, so the reader gets the shape of the day before
        // working through the table.
        const counts = rows.reduce((acc: Record<number, number>, r: any) => {
            const t = Number(r.ExceptionType);
            acc[t] = (acc[t] || 0) + 1;
            return acc;
        }, {});

        const summary = [3, 1, 2]
            .filter(t => counts[t])
            .map(t => `${EXCEPTION_LABELS[t]}: ${counts[t]}`)
            .join('    ');

        doc.text(`Total exceptions: ${rows.length}`, pageWidth - 14, 22, { align: 'right' });
        if (summary) {
            doc.text(summary, 14, 28);
        }

        const tableHeaders = [['Exception', 'Name', 'Site', 'Scheduled Shift', 'Clock In', 'Clock Out']];

        // The proc already orders by ExceptionType then PersonnelName -- preserve it.
        const tableData = rows.map((item: any) => [
            EXCEPTION_LABELS[Number(item.ExceptionType)] ?? String(item.ExceptionType ?? ''),
            item.PersonnelName ?? '',
            item.SiteName ?? '',
            item.ScheduledShift ?? '',
            item.ClockIn ?? '--',
            item.ClockOut ?? '--',
        ]);

        autoTable(doc, {
            startY: 34,
            head: tableHeaders,
            body: tableData,
            theme: 'plain',
            styles: {
                fontSize: 8,
                cellPadding: 0.6,
                lineColor: 40,
                lineWidth: 0.1,
            },
            headStyles: {
                fontStyle: 'bold',
                textColor: 0,
                fillColor: false,
                halign: 'center',
                valign: 'middle',
            },
            columnStyles: {
                0: { cellWidth: 26 },
                1: { cellWidth: 42 },
                2: { cellWidth: 46 },
                3: { cellWidth: 34, halign: 'center' },
                4: { cellWidth: 20, halign: 'center' },
                5: { cellWidth: 20, halign: 'center' },
            },
        });

        addPageNumber(doc);

        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };

    useEffect(() => {
        if (data) {
            generatePDF();
        }
    }, [data]);

    return null;
};

export default memo(DailyReconciliationPdf);
